import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabaseClient'
import ProjectChat from '../components/ProjectChat'
import Header from '../components/Header'
// Use dynamic import to avoid bundling invoicePdf in main chunk
// import { generateInvoicePdf } from '../utils/invoicePdf'

// Helper to lazily load jsPDF from CDN
async function loadJsPDF() {
  if (window.jspdf?.jsPDF) return window.jspdf.jsPDF
  await new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js'
    script.onload = resolve
    script.onerror = reject
    document.body.appendChild(script)
  })
  return window.jspdf.jsPDF
}

const ProjectPage = () => {
  const { id } = useParams()
  const { profile, user } = useAuthStore()
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [project, setProject] = useState(null)
  const [tasks, setTasks] = useState([])
  const [computedProgress, setComputedProgress] = useState(0)
  const [projectFiles, setProjectFiles] = useState([])
  const [projectMembers, setProjectMembers] = useState([])
  const [milestones, setMilestones] = useState([])
  const [client, setClient] = useState(null)
  const [error, setError] = useState(null)
  
  // Task creation states
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignee_id: '',
    priority: 'medium',
    due_date: ''
  })
  const [creatingTask, setCreatingTask] = useState(false)
  
  // File upload states
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    if (id) {
      fetchProjectData()
    }
  }, [id])

  const fetchProjectData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('🔍 Fetching project data for ID:', id)
      
      // Fetch project details
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single()
      
      if (projectError) {
        console.error('❌ Project fetch error:', projectError)
        throw projectError
      }
      
      console.log('✅ Project fetched:', projectData)
      setProject(projectData)
      
      // Fetch client details
      if (projectData.client_id) {
        const { data: clientData, error: clientError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', projectData.client_id)
          .single()
        
        if (!clientError && clientData) {
          setClient(clientData)
        }
      }
      
      // Fetch project tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
      
      if (!tasksError && tasksData) {
        console.log('📋 Tasks fetched successfully:', tasksData.length)
        
        // Fetch assignee details separately
        const assigneeIds = tasksData
          .map(task => task.assignee_id)
          .filter(id => id !== null)
        
        let assignees = []
        if (assigneeIds.length > 0) {
          const { data: assigneeData, error: assigneeError } = await supabase
            .from('profiles')
            .select('id, name, email')
            .in('id', assigneeIds)
          
          if (!assigneeError && assigneeData) {
            assignees = assigneeData
          }
        }
        
        // Combine tasks with assignee details
        const tasksWithAssignees = tasksData.map(task => ({
          ...task,
          assignee: assignees.find(assignee => assignee.id === task.assignee_id) || null
        }))
        
        setTasks(tasksWithAssignees)

        // Compute progress based on completion ratio
        const total = tasksData.length
        const completed = tasksData.filter(t => t.status === 'completed').length
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0
        setComputedProgress(pct)
      } else {
        console.error('❌ Error fetching tasks:', tasksError)
        setTasks([])
        setComputedProgress(0)
      }
      
      // Fetch total payments and invoices
      let totalPaid = 0
      try {
        const { data: paymentsData } = await supabase
          .from('payments')
          .select('amount')
          .eq('project_id', id)
        if (paymentsData && paymentsData.length) {
          totalPaid = paymentsData.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
        }
      } catch {}

      let invoicesList = []
      try {
        const { data: invoicesData } = await supabase
          .from('invoices')
          .select('*')
          .eq('project_id', id)
          .order('created_at', { ascending: false })
        if (invoicesData) invoicesList = invoicesData
      } catch {}

      // Update project with computed totals and invoices
      setProject(prev => ({ ...(prev || projectData), total_paid: totalPaid, invoices: invoicesList }))

      // Fetch project files
      console.log('🔍 Fetching project files for project:', id)
      const { data: filesData, error: filesError } = await supabase
        .from('project_files')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
      
      if (!filesError && filesData) {
        console.log('📁 Files fetched successfully:', filesData.length)
        console.log('📊 Files data:', filesData)
        
        // Fetch uploader details separately
        const uploaderIds = filesData
          .map(file => file.uploaded_by)
          .filter(id => id !== null)
        
        console.log('👤 Uploader IDs:', uploaderIds)
        
        let uploaders = []
        if (uploaderIds.length > 0) {
          const { data: uploaderData, error: uploaderError } = await supabase
            .from('profiles')
            .select('id, name, email')
            .in('id', uploaderIds)
          
          if (!uploaderError && uploaderData) {
            uploaders = uploaderData
            console.log('✅ Uploader details fetched:', uploaders.length)
          } else {
            console.error('❌ Error fetching uploader details:', uploaderError)
          }
        }
        
        // Combine files with uploader details
        const filesWithUploaders = filesData.map(file => ({
          ...file,
          uploader: uploaders.find(uploader => uploader.id === file.uploaded_by) || null
        }))
        
        console.log('✅ Combined files with uploaders:', filesWithUploaders)
        setProjectFiles(filesWithUploaders)
      } else {
        console.error('❌ Error fetching files:', filesError)
        console.log('📊 Files data received:', filesData)
        setProjectFiles([])
      }
      
      // Fetch project members
      console.log('🔍 Fetching project members for project:', id)
      
      const { data: membersData, error: membersError } = await supabase
        .from('project_members')
        .select('*')
        .eq('project_id', id)
      
      if (!membersError && membersData) {
        console.log('👥 Project members fetched successfully:', membersData)
        console.log('📊 Number of members:', membersData.length)
        
        // Fetch user details separately
        const userIds = membersData.map(member => member.user_id)
        if (userIds.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('profiles')
            .select('id, name, email, role')
            .in('id', userIds)
          
          if (!usersError && usersData) {
            console.log('✅ User details fetched:', usersData.length)
            
            // Combine project members with user details
            const combinedData = membersData.map(member => ({
              ...member,
              user: usersData.find(user => user.id === member.user_id)
            }))
            
            console.log('✅ Combined project members data:', combinedData)
            setProjectMembers(combinedData)
          } else {
            console.error('❌ Error fetching user details:', usersError)
            setProjectMembers(membersData)
          }
        } else {
          console.log('ℹ️ No project members found')
          setProjectMembers([])
        }
      } else {
        console.error('❌ Error fetching project members:', membersError)
        console.log('📊 Members data received:', membersData)
        setProjectMembers([])
      }
      
      // Fetch milestones
      const { data: milestonesData, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('project_id', id)
        .order('due_date', { ascending: true })
      
      if (!milestonesError && milestonesData) {
        setMilestones(milestonesData)
      }
      
    } catch (error) {
      console.error('❌ Error fetching project data:', error)
      setError('Failed to load project data')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100'
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100'
      case 'pending':
        return 'text-red-600 bg-red-100'
      case 'active':
        return 'text-green-600 bg-green-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100'
      case 'low':
        return 'text-green-600 bg-green-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  // Check if user can create tasks (admin or assigned employee)
  const canCreateTasks = () => {
    if (!profile) return false
    if (profile.role === 'admin') return true
    
    // Check if user is assigned to this project
    const isAssigned = projectMembers.some(member => member.user_id === profile.id)
    return isAssigned
  }

  // Handle task creation
  const handleCreateTask = async (e) => {
    e.preventDefault()
    if (!canCreateTasks()) return

    setCreatingTask(true)
    try {
      const taskData = {
        project_id: id,
        title: newTask.title,
        description: newTask.description,
        assignee_id: newTask.assignee_id || null,
        priority: newTask.priority,
        due_date: newTask.due_date || null,
        status: 'pending',
        created_by: profile.id
      }

      console.log('📝 Creating task:', taskData)

      const { data, error } = await supabase
        .from('tasks')
        .insert([taskData])
        .select()

      if (error) {
        console.error('❌ Error creating task:', error)
        alert('Failed to create task: ' + error.message)
        return
      }

      console.log('✅ Task created successfully:', data)
      
      // Reset form
      setNewTask({
        title: '',
        description: '',
        assignee_id: '',
        priority: 'medium',
        due_date: ''
      })
      setShowTaskForm(false)
      
      // Refresh tasks
      await fetchProjectData()
      
      alert('Task created successfully!')
      
    } catch (error) {
      console.error('❌ Error creating task:', error)
      alert('Failed to create task: ' + error.message)
    } finally {
      setCreatingTask(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault()
    if (!canCreateTasks()) return
    if (!selectedFile) return

    setUploadingFile(true)
    try {
      console.log('📁 Uploading file:', selectedFile.name)

      // Upload file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `${id}/${fileName}` // Remove 'project-files/' prefix

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-files')
        .upload(filePath, selectedFile)

      if (uploadError) {
        console.error('❌ Error uploading file:', uploadError)
        alert('Failed to upload file: ' + uploadError.message)
        return
      }

      console.log('✅ File uploaded successfully:', uploadData)

      // Save file info to database
      const fileData = {
        project_id: id,
        filename: selectedFile.name,
        file_path: filePath,
        file_size: selectedFile.size,
        file_type: selectedFile.type,
        uploaded_by: profile.id
      }

      const { data: dbData, error: dbError } = await supabase
        .from('project_files')
        .insert([fileData])
        .select()

      if (dbError) {
        console.error('❌ Error saving file info:', dbError)
        alert('Failed to save file info: ' + dbError.message)
        return
      }

      console.log('✅ File info saved successfully:', dbData)
      
      // Reset form
      setSelectedFile(null)
      setShowFileUpload(false)
      
      // Refresh files
      await fetchProjectData()
      
      alert('File uploaded successfully!')
      
    } catch (error) {
      console.error('❌ Error uploading file:', error)
      alert('Failed to upload file: ' + error.message)
    } finally {
      setUploadingFile(false)
    }
  }

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      console.log('📥 Downloading file:', file.filename)
      console.log('📁 File path:', file.file_path)
      console.log('📊 File object:', file)
      
      // Try to get download URL from Supabase Storage
      const { data, error } = await supabase.storage
        .from('project-files')
        .createSignedUrl(file.file_path, 60) // 60 seconds expiry
      
      if (error) {
        console.error('❌ Error creating download URL:', error)
        console.error('❌ Error details:', error.message)
        
        // Try alternative method - direct download
        console.log('🔄 Trying alternative download method...')
        
        try {
          const { data: downloadData, error: downloadError } = await supabase.storage
            .from('project-files')
            .download(file.file_path)
          
          if (downloadError) {
            console.error('❌ Alternative download failed:', downloadError)
            alert('Failed to download file: ' + downloadError.message)
            return
          }
          
          console.log('✅ Alternative download successful:', downloadData)
          
          // Create blob URL and trigger download
          const url = URL.createObjectURL(downloadData)
          const link = document.createElement('a')
          link.href = url
          link.download = file.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          
        } catch (altError) {
          console.error('❌ Alternative download error:', altError)
          alert('Failed to download file: ' + altError.message)
        }
        
        return
      }
      
      console.log('✅ Download URL created:', data.signedUrl)
      
      // Create temporary link and trigger download
      const link = document.createElement('a')
      link.href = data.signedUrl
      link.download = file.filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
    } catch (error) {
      console.error('❌ Error downloading file:', error)
      alert('Failed to download file: ' + error.message)
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const downloadInvoicePdf = async (inv) => {
    try {
      const { generateInvoicePdf } = await import('../utils/invoicePdf')
      await generateInvoicePdf({ invoice: inv, project, client })
    } catch (e) {
      console.error('Failed to generate PDF:', e)
      alert('Failed to generate PDF. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The project you are looking for does not exist.'}</p>
          <Link 
            to={profile?.role === 'admin' ? '/admin' : profile?.role === 'employee' ? '/employee' : '/client'} 
            className="btn-primary"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Website Header */}
      <Header />

      {/* Project Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.name}</h1>
              <p className="text-gray-600 mb-4">{project.description}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                {(profile?.role === 'admin' || profile?.role === 'client') && (
                  <>
                    <span>Budget: ৳{project.budget?.toLocaleString() || '0'}</span>
                    <span className="font-semibold text-red-600">
                      Due: ৳{Math.max(((project?.budget || 0) - (project?.total_paid || 0)), 0).toLocaleString()}
                    </span>
                  </>
                )}
                <span>Due: {formatDate(project.end_date)}</span>
                <span className={`font-semibold ${getStatusColor(project.status)} px-2 py-1 rounded`}>
                  {project.status}
                </span>
                <span className={`font-semibold ${getPriorityColor(project.priority)} px-2 py-1 rounded`}>
                  {project.priority || 'medium'}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Progress</div>
              <div className="text-2xl font-bold text-primary-600">{computedProgress}%</div>
              <div className="w-32 bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full" 
                  style={{width: `${computedProgress}%`}}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'chat'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Chat
            </button>
            {(profile?.role === 'admin' || profile?.role === 'client') && (
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payments
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'chat' ? (
          <div className="card min-h-[90vh]">
            <ProjectChat projectId={id} />
          </div>
        ) : activeTab === 'payments' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="card">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Payments</h2>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded border border-gray-200">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Total Budget</span>
                      <span>৳{project?.budget?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>Total Paid</span>
                      <span>৳{(project?.total_paid || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold text-gray-800 mt-1">
                      <span>Due</span>
                      <span>৳{((project?.budget || 0) - (project?.total_paid || 0)).toLocaleString()}</span>
                    </div>
                  </div>

                  {(profile?.role === 'admin') && (
                    <form onSubmit={async (e) => {
                      e.preventDefault()
                      const formEl = e.currentTarget
                      const form = new FormData(formEl)
                      const amount = parseFloat(form.get('amount'))
                      if (!amount || amount <= 0) return
                      try {
                        const payload = { project_id: id, amount, created_by: user?.id || null }
                        console.log('➕ Adding payment payload:', payload)
                        const { data: payData, error: payError } = await supabase
                          .from('payments')
                          .insert(payload)
                          .select()
                          .single()
                        if (payError) {
                          console.error('❌ Add payment error:', payError)
                          return
                        }
                        console.log('✅ Payment inserted:', payData)
                        const newTotal = (project?.total_paid || 0) + amount
                        setProject(prev => ({ ...prev, total_paid: newTotal }))
                        if (formEl && typeof formEl.reset === 'function') formEl.reset()
                      } catch (err) {
                        console.error('❌ Add payment failed:', err)
                      }
                    }} className="flex items-center space-x-3">
                      <input name="amount" type="number" step="0.01" min="0" placeholder="Amount (৳)" className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
                      <button type="submit" className="btn-primary">Add Payment</button>
                    </form>
                  )}

                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Invoices</h3>
                    <div className="space-y-2">
                      {(project?.invoices || []).length === 0 ? (
                        <p className="text-sm text-gray-500">No invoices yet</p>
                      ) : (
                        project.invoices.map(inv => (
                          <div key={inv.id} className="flex justify-between items-center text-sm border rounded p-2">
                            <span>Invoice #{inv.id} • ৳{inv.amount.toLocaleString()}</span>
                            {(profile?.role === 'admin' || profile?.role === 'client') && (
                              <button onClick={() => downloadInvoicePdf(inv)} className="text-primary-600 hover:underline">Download PDF</button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar (Client Info re-used) */}
            <div className="space-y-8">
              {client && (
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Client</h3>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-medium">{client.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'C'}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{client.name}</p>
                      <p className="text-xs text-gray-600">{client.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Tasks Section */}
              <div className="card">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Tasks</h2>
                  {canCreateTasks() && (
                    <button 
                      onClick={() => setShowTaskForm(true)}
                      className="btn-primary"
                    >
                      Add Task
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No tasks assigned yet</p>
                    </div>
                  ) : (
                    tasks.map(task => (
                      <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium">{task.title}</h3>
                          <span className={`text-sm px-2 py-1 rounded ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                        <div className="flex justify-between items-center text-sm text-gray-500">
                          <span>Assigned to: {task.assignee?.name || 'Unassigned'}</span>
                          <span>Due: {formatDate(task.due_date)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Files Section */}
              <div className="card">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Files</h2>
                  {canCreateTasks() && (
                    <button 
                      onClick={() => setShowFileUpload(true)}
                      className="btn-primary"
                    >
                      Upload File
                    </button>
                  )}
                </div>
                
                <div className="space-y-3">
                  {projectFiles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No files uploaded yet</p>
                    </div>
                  ) : (
                    projectFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="font-medium">{file.filename}</p>
                            <p className="text-sm text-gray-600">
                              {formatFileSize(file.file_size)} • Uploaded by {file.uploader?.name || 'Unknown'} • {new Date(file.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleFileDownload(file)}
                          className="btn-secondary text-sm"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Project Members */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Team Members</h3>
                <div className="space-y-3">
                  {projectMembers.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">No team members assigned</p>
                    </div>
                  ) : (
                    projectMembers.map(member => (
                      <div key={member.id} className="flex items-center">
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white text-sm font-medium">
                            {member.user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{member.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-600">{member.role}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Client Info */}
              {client && (
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Client</h3>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-medium">
                        {client.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{client.name}</p>
                      <p className="text-xs text-gray-600">{client.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Milestones */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Milestones</h3>
                <div className="space-y-3">
                  {milestones.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">No milestones set</p>
                    </div>
                  ) : (
                    milestones.map(milestone => (
                      <div key={milestone.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{milestone.title}</p>
                          <p className="text-xs text-gray-600">Due: {formatDate(milestone.due_date)}</p>
                        </div>
                        <span className={`text-sm px-2 py-1 rounded ${getStatusColor(milestone.status)}`}>
                          {milestone.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Creation Modal */}
        {showTaskForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Create New Task</h3>
              <form onSubmit={handleCreateTask}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Title
                    </label>
                    <input
                      type="text"
                      value={newTask.title}
                      onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assignee
                    </label>
                    <select
                      value={newTask.assignee_id}
                      onChange={(e) => setNewTask({...newTask, assignee_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Assignee</option>
                      {projectMembers.map(member => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user?.name || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowTaskForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingTask}
                    className="btn-primary disabled:opacity-50"
                  >
                    {creatingTask ? 'Creating...' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* File Upload Modal */}
        {showFileUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Upload File</h3>
              <form onSubmit={handleFileUpload}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select File
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                    {selectedFile && (
                      <p className="text-sm text-gray-600 mt-1">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowFileUpload(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingFile || !selectedFile}
                    className="btn-primary disabled:opacity-50"
                  >
                    {uploadingFile ? 'Uploading...' : 'Upload File'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectPage